import React, { useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { UserContext } from '../../../contexts/user-context';
import { formatDateForDisplay } from '../../../helpers/date-time';
import { isValidEmail } from '../../../helpers/form-validation';
import { fetchData } from '../../../helpers/methods';

import Button from '../../../component-library/button';
import ButtonIcon from '../../../component-library/button-icon';
import Icon from '../../../component-library/icon';
import Modal from '../../../component-library/modal';
import FormField from '../../../component-library/form-field';
import SpinnerContainer from '../../../component-library/spinner-container';

const ManageAccess = () => {
	const {
		email: userEmail,
	} = useContext(UserContext);
	const { id } = useParams();
	const [loading, setLoading] = useState(false);
	const [email, setEmail] = useState(userEmail || '');
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [username, setUsername] = useState('');
	const [applicationName, setApplicationName] = useState('');
	const [addCredentialsApp, setAddCredentialsApp] = useState('');
	const [useCredentials, setUseCredentials] = useState({});
	const [removeApplication, setRemoveApplication] = useState({});
	const [removeCredentials, setRemoveCredentials] = useState({});
	const [disableModalButton, setDisableModalButton] = useState(false);
	const [showAddApplication, setShowAddApplication] = useState(false);
	const [showAddCredentials, setShowAddCredentials] = useState(false);
	const [showUseCredentials, setShowUseCredentials] = useState(false);
	const [showRemoveProduct, setShowRemoveProduct] = useState(false);
	const [showRemoveCredentials, setShowRemoveCredentials] = useState(false);
	const [showRemoveApplication, setShowRemoveApplication] = useState(false);
	const [showRemoveDeveloper, setShowRemoveDeveloper] = useState(false);
	const [noEmail, setNoEmail] = useState(false);
	const [emailError, setEmailError] = useState('');
	const [firstNameError, setFirstNameError] = useState('');
	const [lastNameError, setLastNameError] = useState('');
	const [usernameError, setUsernameError] = useState('');
	const [addCredentialsError, setAddCredentialsError] = useState('');
	const [removeCredentialsError, setRemoveCredentialsError] = useState('');
	const [removeDeveloperError, setRemoveDeveloperError] = useState('');
	const [useCredentialsError, setUseCredentialsError] = useState('');
	const [removeApplicationError, setRemoveApplicationError] = useState('');
	const [removeExistingProductError, setRemoveExistingProductError] = useState('');
	const [applicationNameError, setApplicationNameError] = useState('');
	const [data, setData] = useState({});

	const getDeveloperData = () => {
		setLoading(true);
		fetchData({
			uri: `/api/web/catalog/item/${id}/runtime`,
			method: 'GET',
		})
			.then(({ ok, body }) => {
				if (ok) {
					setData(body);
					setLoading(false);
				} else {
					if (body.message === 'No associated email address') {
						setNoEmail(true);
					} else {
						setEmailError('We could not retrieve your details at this time, please try again');
					}
					setLoading(false);
				}
			})
			.catch(() => {
				setEmailError('We could not retrieve your details at this time, please try again');
				setLoading(false);
			});
	};

	useEffect(getDeveloperData, []);

	const createNewDeveloper = () => {
		const developerData = {
			email,
			firstName,
			lastName,
			username,
			apps: [
				{
					name: applicationName,
					credentials: [{}],
				},
			],
		};
		setLoading(true);
		fetchData({
			uri: `/api/web/catalog/item/${id}/runtime/${email}/platformRuntime/developer`,
			method: 'POST',
			options: {
				body: JSON.stringify(developerData),
			},
		})
			.then(({ ok }) => {
				if (ok) {
					setApplicationName('');
					getDeveloperData();
				} else {
					setLoading(false);
				}
			})
			.catch(() => {
				setLoading(false);
			});
	};

	const createNewApplication = appName => {
		setDisableModalButton(true);
		const developerData = data.platformRuntime.developer;
		const applicationData = {
			email: developerData.email,
			firstName: developerData.firstName,
			lastName: developerData.lastName,
			username: developerData.username,
			apps: [
				{
					name: appName,
					credentials: [{}],
				},
			],
		};
		fetchData({
			uri: `/api/web/catalog/item/${id}/runtime/${email}/platformRuntime/developer`,
			method: 'PUT',
			options: {
				body: JSON.stringify(applicationData),
			},
		})
			.then(({ ok }) => {
				if (ok) {
					setLoading(true);
					setShowAddApplication(false);
					setDisableModalButton(false);
					getDeveloperData();
					setAddCredentialsApp('');
					setApplicationName('');
					setApplicationNameError('');
				} else {
					setDisableModalButton(false);
					setApplicationNameError('We could not create your application at this time, please try again');
				}
			})
			.catch(() => {
				setDisableModalButton(false);
				setApplicationNameError('We could not create your application at this time, please try again');
			});
	};

	const addCredentialsToApplication = appName => {
		setDisableModalButton(true);
		const developerData = data.platformRuntime.developer;
		const applicationData = {
			email: developerData.email,
			firstName: developerData.firstName,
			lastName: developerData.lastName,
			username: developerData.username,
			apps: [
				{
					name: appName,
					credentials: [{}],
				},
			],
		};
		fetchData({
			uri: `/api/web/catalog/item/${id}/runtime/${email}/platformRuntime/developer`,
			method: 'PUT',
			options: {
				body: JSON.stringify(applicationData),
			},
		})
			.then(({ ok }) => {
				if (ok) {
					setLoading(true);
					setShowAddCredentials(false);
					setDisableModalButton(false);
					getDeveloperData();
					setAddCredentialsApp('');
					setAddCredentialsError('');
				} else {
					setDisableModalButton(false);
					setAddCredentialsError('We could not add credentials to your application at this time, please try again');
				}
			})
			.catch(() => {
				setDisableModalButton(false);
				setAddCredentialsError('We could not add credentials to your application at this time, please try again');
			});
	};

	const useExistingCredentials = (credentials, appName) => {
		setDisableModalButton(true);
		const developerData = data.platformRuntime.developer;
		const productName = data.platformRuntime.apiProduct.name;
		const newCredentials = {
			consumerKey: credentials.consumerKey,
			products: [{
				apiProduct: productName,
			}],
		};
		const applicationData = {
			email: developerData.email,
			firstName: developerData.firstName,
			lastName: developerData.lastName,
			username: developerData.username,
			apps: [
				{
					name: appName,
					credentials: [newCredentials],
				},
			],
		};
		fetchData({
			uri: `/api/web/catalog/item/${id}/runtime/${email}/platformRuntime/developer`,
			method: 'PUT',
			options: {
				body: JSON.stringify(applicationData),
			},
		})
			.then(({ ok }) => {
				if (ok) {
					setLoading(true);
					getDeveloperData();
					setShowUseCredentials(false);
					setDisableModalButton(false);
					setAddCredentialsApp('');
					setUseCredentials({});
					setUseCredentialsError('');
				} else {
					setDisableModalButton(false);
					setUseCredentialsError('We could not update your application at this time, please try again');
				}
			})
			.catch(() => {
				setDisableModalButton(false);
				setUseCredentialsError('We could not update your application at this time, please try again');
			});
	};

	const removeExistingCredentials = (credentials, appName) => {
		setDisableModalButton(true);
		const developerData = data.platformRuntime.developer;
		const delCredentials = {
			consumerKey: credentials.consumerKey,
			deleted: true,
		};
		const applicationData = {
			email: developerData.email,
			firstName: developerData.firstName,
			lastName: developerData.lastName,
			username: developerData.username,
			apps: [
				{
					name: appName,
					credentials: [delCredentials],
				},
			],
		};
		fetchData({
			uri: `/api/web/catalog/item/${id}/runtime/${email}/platformRuntime/developer`,
			method: 'PUT',
			options: {
				body: JSON.stringify(applicationData),
			},
		})
			.then(({ ok }) => {
				if (ok) {
					setLoading(true);
					getDeveloperData();
					setShowRemoveCredentials(false);
					setDisableModalButton(false);
					setRemoveCredentials({});
					setRemoveCredentialsError('');
				} else {
					setDisableModalButton(false);
					setRemoveCredentialsError('We could not remove your credentials at this time, please try again');
				}
			})
			.catch(() => {
				setDisableModalButton(false);
				setRemoveCredentialsError('We could not remove your credentials at this time, please try again');
			});
	};

	const removeExistingProduct = (credentials, appName) => {
		setDisableModalButton(true);
		const developerData = data.platformRuntime.developer;
		const productName = data.platformRuntime.apiProduct.name;
		const newCredentials = {
			consumerKey: credentials.consumerKey,
			products: [{
				apiProduct: productName,
				deleted: true,
			}],
		};
		const applicationData = {
			email: developerData.email,
			firstName: developerData.firstName,
			lastName: developerData.lastName,
			username: developerData.username,
			apps: [
				{
					name: appName,
					credentials: [newCredentials],
				},
			],
		};
		fetchData({
			uri: `/api/web/catalog/item/${id}/runtime/${email}/platformRuntime/developer`,
			method: 'PUT',
			options: {
				body: JSON.stringify(applicationData),
			},
		})
			.then(({ ok }) => {
				if (ok) {
					setLoading(true);
					getDeveloperData();
					setShowRemoveProduct(false);
					setDisableModalButton(false);
					setRemoveApplication('');
					setRemoveCredentials({});
					setRemoveExistingProductError('');
				} else {
					setDisableModalButton(false);
					setRemoveExistingProductError('We could not remove your credentials at this time, please try again');
				}
			})
			.catch(() => {
				setDisableModalButton(false);
				setRemoveExistingProductError('We could not remove your credentials at this time, please try again');
			});
	};

	const removeExistingApplication = appName => {
		setDisableModalButton(true);
		const developerData = data.platformRuntime.developer;
		const applicationData = {
			email: developerData.email,
			firstName: developerData.firstName,
			lastName: developerData.lastName,
			username: developerData.username,
			apps: [
				{
					name: appName,
					deleted: true,
				},
			],
		};
		fetchData({
			uri: `/api/web/catalog/item/${id}/runtime/${email}/platformRuntime/developer`,
			method: 'PUT',
			options: {
				body: JSON.stringify(applicationData),
			},
		})
			.then(({ ok }) => {
				if (ok) {
					setLoading(true);
					getDeveloperData();
					setShowRemoveApplication(false);
					setDisableModalButton(false);
					setRemoveApplication({});
					setRemoveApplicationError('');
				} else {
					setDisableModalButton(false);
					setRemoveApplicationError('We could not remove your application at this time, please try again');
				}
			})
			.catch(() => {
				setDisableModalButton(false);
				setRemoveApplicationError('We could not remove your application at this time, please try again');
			});
	};

	const removeExistingDeveloper = () => {
		setDisableModalButton(true);
		const developerData = data.platformRuntime.developer;
		const applicationData = {
			email: developerData.email,
			firstName: developerData.firstName,
			lastName: developerData.lastName,
			username: developerData.username,
			deleted: true,
		};
		fetchData({
			uri: `/api/web/catalog/item/${id}/runtime/${email}/platformRuntime/developer`,
			method: 'PUT',
			options: {
				body: JSON.stringify(applicationData),
			},
		})
			.then(({ ok }) => {
				if (ok) {
					setLoading(true);
					getDeveloperData();
					setShowRemoveDeveloper(false);
					setDisableModalButton(false);
					setRemoveDeveloperError('');
				} else {
					setDisableModalButton(false);
					setRemoveDeveloperError('We could not remove this developer at this time, please try again');
				}
			})
			.catch(() => {
				setDisableModalButton(false);
				setRemoveDeveloperError('We could not remove this developer at this time, please try again');
			});
	};

	const handleNewUserValidation = () => {
		let error = false;
		setEmailError('');
		setFirstNameError('');
		setLastNameError('');
		setUsernameError('');
		if (email === '' || !isValidEmail(email)) {
			setEmailError('You must enter a valid email.');
			error = true;
		}
		if (firstName === '') {
			setFirstNameError('You must enter a first name.');
			error = true;
		}
		if (lastName === '') {
			setLastNameError('You must enter a last name.');
			error = true;
		}
		if (username === '') {
			setUsernameError('You must enter a username.');
			error = true;
		}
		if (username === '') {
			setApplicationNameError('You must enter an application name.');
			error = true;
		}
		if (!error) {
			setEmailError('');
			setFirstNameError('');
			setLastNameError('');
			setUsernameError('');
			setApplicationNameError('');
			createNewDeveloper();
		}
	};

	const handleNewApplicationValidation = () => {
		let error = false;
		setApplicationNameError('');
		if (applicationName === '') {
			setApplicationNameError('You must enter a name for your application.');
			error = true;
		}
		if (!error) {
			setApplicationNameError('');
			createNewApplication(applicationName);
		}
	};

	const handleAddCredentials = app => {
		setAddCredentialsApp(app.name);
		setShowAddCredentials(true);
	};

	const handleUseCredentials = (credentials, app) => {
		setAddCredentialsApp(app.name);
		setUseCredentials(credentials);
		setShowUseCredentials(true);
	};

	const handleRemoveProduct = (credentials, app) => {
		setRemoveApplication(app.name);
		setRemoveCredentials(credentials);
		setShowRemoveProduct(true);
	};

	const handleRemoveCredentials = (credentials, app) => {
		setRemoveApplication(app.name);
		setRemoveCredentials(credentials);
		setShowRemoveCredentials(true);
	};

	const handleRemoveApplication = app => {
		setRemoveApplication(app.name);
		setShowRemoveApplication(true);
	};

	const checkActiveRow = products => {
		if (products.some(product => product.apiProduct === data.platformRuntime.apiProduct.name)) {
			return 'is-active-row';
		}

		return 'is-inactive-row';
	};

	return (
		<>
			<h3>Manage Access</h3>
			{loading && (
				<SpinnerContainer />
			)}
			{!loading && noEmail && (
				<>
					<p>Your account must have an email address set to use this feature.</p>
					<p>Please contact your administrator for support.</p>
				</>
			)}
			{!loading && Object.keys(data).length && !data.platformRuntime.developer ? (
				<>
					<p>
						You must create a developer and application before generating API keys
					</p>
					<FormField
						type="email"
						label="Email address"
						action={e => { setEmail(e.target.value); }}
						id="email_field"
						name="email_field"
						value={email}
						error={emailError}
						fullWidth
						disabled
					/>
					<FormField
						type="text"
						label="First Name"
						action={e => { setFirstName(e.target.value); }}
						id="first_name_field"
						name="first_name_field"
						value={firstName}
						error={firstNameError}
						required
						fullWidth
					/>
					<FormField
						type="text"
						label="Last Name"
						action={e => { setLastName(e.target.value); }}
						id="last_name_field"
						name="last_name_field"
						value={lastName}
						error={lastNameError}
						required
						fullWidth
					/>
					<FormField
						type="text"
						label="Username"
						action={e => { setUsername(e.target.value); }}
						id="username_field"
						name="username_field"
						value={username}
						error={usernameError}
						required
						fullWidth
					/>
					<FormField
						type="text"
						label="Application Name"
						action={e => { setApplicationName(e.target.value); }}
						id="application_name_field"
						name="application_name_field"
						value={applicationName}
						error={applicationNameError}
						required
						fullWidth
					/>
					<div className="text-right margin-bottom-16">
						<Button
							type="primary"
							label="Continue"
							onClick={() => handleNewUserValidation()}
							classes="margin-left-16"
						/>
					</div>
				</>
			) : null}

			{!loading && Object.keys(data).length && data.platformRuntime.developer ? (
				<>
					<h4>Developer Details</h4>
					<table className="table is-bordered margin-bottom-32">
						<thead>
							<tr>
								<th>Name</th>
								<th>Email</th>
								<th>Username</th>
								<th>Status</th>
								<th> </th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td>
									{data.platformRuntime.developer.firstName}
									{' '}
									{data.platformRuntime.developer.lastName}
								</td>
								<td>{data.platformRuntime.developer.email}</td>
								<td>{data.platformRuntime.developer.username}</td>
								<td className="text-capitalize">{data.platformRuntime.developer.status}</td>
								<td className="text-right">
									<ButtonIcon
										icon="trash"
										title="Remove Developer"
										type="danger"
										onClick={() => setShowRemoveDeveloper(true)}
									/>
								</td>
							</tr>
						</tbody>
					</table>
					<div className="flex-row justify-space-between">
						<h4>Applications</h4>
						<ButtonIcon
							icon="plus"
							label="New Application"
							onClick={() => setShowAddApplication(true)}
						/>
					</div>
					{data.platformRuntime.developer && data.platformRuntime.developer.apps.length ? (
						data.platformRuntime.developer.apps.map((item, index) => (
							// eslint-disable-next-line react/no-array-index-key
							<div key={index}>
								<h5>{item.name}</h5>
								<table className="table is-bordered margin-bottom-4">
									<thead>
										<tr>
											<th>Consumer Key</th>
											<th>Issued</th>
											<th>Expires</th>
											<th>Status</th>
											<th> </th>
										</tr>
									</thead>
									<tbody>
										{item.credentials.length ? (
											item.credentials.map((credential, itemIndex) => (
												// eslint-disable-next-line react/no-array-index-key
												<tr key={itemIndex} className={checkActiveRow(credential.products)}>
													<td
														className="can-copy"
														role="presentation"
														onClick={() => {
															navigator.clipboard.writeText(credential.consumerKey);
														}}
													>
														{credential.consumerKey}
														<Icon icon="copy" title="Click to Copy" />
													</td>
													<td>{credential.issuedAt === null ? 'N/A' : formatDateForDisplay(credential.issuedAt)}</td>
													<td>{credential.expiresAt === null ? 'Never' : formatDateForDisplay(credential.expiresAt)}</td>
													<td className="text-capitalize">{credential.status}</td>
													<td className="text-right">
														{checkActiveRow(credential.products) !== 'is-active-row' && (
															<ButtonIcon
																icon="id-card"
																title="Use These Credentials"
																onClick={() => handleUseCredentials(credential, item)}
															/>
														)}
														{checkActiveRow(credential.products) !== 'is-inactive-row' && (
															<ButtonIcon
																icon="id-card"
																title="Remove From Application"
																type="danger"
																onClick={() => handleRemoveProduct(credential, item)}
															/>
														)}
														{data.storefront.runtime === 'Apigee' && (
															<ButtonIcon
																icon="trash"
																title="Remove Credentials"
																type="danger"
																classes="margin-left-8"
																onClick={() => handleRemoveCredentials(credential, item)}
															/>
														)}
													</td>
												</tr>
											))
										) : (
											<tr>
												<td colSpan={4} className="grey-text text-center">No credentials found</td>
											</tr>
										)}
									</tbody>
								</table>
								<div className="flex-row justify-space-between margin-bottom-32">
									<ButtonIcon
										icon="trash"
										label="Remove Application"
										type="danger"
										onClick={() => handleRemoveApplication(item)}
									/>
									{data.storefront.runtime === 'Apigee' && (
										<ButtonIcon
											icon="plus"
											label="New Credentials"
											onClick={() => handleAddCredentials(item)}
										/>
									)}
								</div>
							</div>
						))
					) : (
						<>
							<p>You have no applications, would you like to create a new one?</p>
							<FormField
								type="text"
								label="Application Name"
								action={e => { setApplicationName(e.target.value); }}
								id="application_name_field"
								name="application_name_field"
								value={applicationName}
								error={applicationNameError}
								required
								fullWidth
							/>
							<div className="text-right margin-bottom-16">
								<Button
									type="primary"
									label="Continue"
									onClick={() => handleNewApplicationValidation()}
								/>
							</div>
						</>
					)}
				</>
			) : null}
			{showAddApplication && (
				<Modal
					title="Add a New Application"
					handleCancel={() => setShowAddApplication(false)}
					isLarge={false}
				>
					<>
						<FormField
							type="text"
							label="Application Name"
							action={e => { setApplicationName(e.target.value); }}
							id="application_name_field"
							name="application_name_field"
							value={applicationName}
							error={applicationNameError}
							required
							fullWidth
						/>
						<div className="text-right margin-bottom-16">
							<Button
								type="primary"
								label="Continue"
								isDisabled={disableModalButton}
								onClick={() => handleNewApplicationValidation()}
							/>
						</div>
					</>
				</Modal>
			)}
			{showAddCredentials && (
				<Modal
					title="Add New Credentials"
					handleCancel={() => setShowAddCredentials(false)}
					isLarge={false}
					buttons={(
						<>
							<Button
								type="secondary"
								label="Cancel"
								onClick={() => setShowAddCredentials(false)}
								classes=""
							/>
							<Button
								type="primary"
								label="Continue"
								isDisabled={disableModalButton}
								onClick={() => addCredentialsToApplication(addCredentialsApp)}
								classes="margin-left-16"
							/>
						</>
					)}
				>
					<p>
						Are you sure you would like to add new credentials to
						<strong>
							{' '}
							{addCredentialsApp}
						</strong>
						?
					</p>
					{addCredentialsError !== '' && (
						<p className="font-color-theme-danger">{addCredentialsError}</p>
					)}
				</Modal>
			)}
			{showUseCredentials && (
				<Modal
					title="Use Existing Credentials"
					handleCancel={() => setShowUseCredentials(false)}
					isLarge={false}
					buttons={(
						<>
							<Button
								type="secondary"
								label="Cancel"
								onClick={() => setShowUseCredentials(false)}
								classes=""
							/>
							<Button
								type="primary"
								label="Continue"
								isDisabled={disableModalButton}
								onClick={() => useExistingCredentials(useCredentials, addCredentialsApp)}
								classes="margin-left-16"
							/>
						</>
					)}
				>
					<p>
						Are you sure you would like to use these credentials?
					</p>
					{useCredentialsError !== '' && (
						<p className="font-color-theme-danger">{useCredentialsError}</p>
					)}
				</Modal>
			)}
			{showRemoveProduct && (
				<Modal
					title="Remove from Application"
					handleCancel={() => setShowRemoveProduct(false)}
					isLarge={false}
					buttons={(
						<>
							<Button
								type="secondary"
								label="Cancel"
								onClick={() => setShowRemoveProduct(false)}
								classes=""
							/>
							<Button
								type="primary"
								label="Continue"
								isDisabled={disableModalButton}
								onClick={() => removeExistingProduct(removeCredentials, removeApplication)}
								classes="margin-left-16"
							/>
						</>
					)}
				>
					<p>
						Are you sure you would like to remove these credentials from
						<strong>
							{' '}
							{removeApplication}
						</strong>
						?
					</p>
					{removeExistingProductError !== '' && (
						<p className="font-color-theme-danger">{removeExistingProductError}</p>
					)}
				</Modal>
			)}
			{showRemoveCredentials && (
				<Modal
					title="Remove Credentials"
					handleCancel={() => setShowRemoveCredentials(false)}
					isLarge={false}
					buttons={(
						<>
							<Button
								type="secondary"
								label="Cancel"
								onClick={() => setShowRemoveCredentials(false)}
								classes=""
							/>
							<Button
								type="primary"
								label="Continue"
								isDisabled={disableModalButton}
								onClick={() => removeExistingCredentials(removeCredentials, removeApplication)}
								classes="margin-left-16"
							/>
						</>
					)}
				>
					<p>
						Are you sure you would like to remove these credentials?
					</p>
					<p className="font-color-theme-danger">
						This will permanently remove these credentials from
						{' '}
						<strong className="font-color-theme-danger">ALL</strong>
						{' '}
						apps that currently use them.
					</p>
					{removeCredentialsError !== '' && (
						<p className="font-color-theme-danger">{removeCredentialsError}</p>
					)}
				</Modal>
			)}
			{showRemoveApplication && (
				<Modal
					title="Remove Application"
					handleCancel={() => setShowRemoveApplication(false)}
					isLarge={false}
					buttons={(
						<>
							<Button
								type="secondary"
								label="Cancel"
								onClick={() => setShowRemoveApplication(false)}
								classes=""
							/>
							<Button
								type="primary"
								label="Continue"
								isDisabled={disableModalButton}
								onClick={() => removeExistingApplication(removeApplication)}
								classes="margin-left-16"
							/>
						</>
					)}
				>
					<p>
						Are you sure you would like to remove
						<strong>
							{' '}
							{removeApplication}
						</strong>
						?
					</p>
					{removeApplicationError !== '' && (
						<p className="font-color-theme-danger">{removeApplicationError}</p>
					)}
				</Modal>
			)}
			{showRemoveDeveloper && (
				<Modal
					title="Remove Developer"
					handleCancel={() => setShowRemoveDeveloper(false)}
					isLarge={false}
					buttons={(
						<>
							<Button
								type="secondary"
								label="Cancel"
								onClick={() => setShowRemoveDeveloper(false)}
								classes=""
							/>
							<Button
								type="primary"
								label="Continue"
								isDisabled={disableModalButton}
								onClick={() => removeExistingDeveloper()}
								classes="margin-left-16"
							/>
						</>
					)}
				>
					<p>
						Are you sure you would like to remove this developer?
					</p>
					{removeDeveloperError !== '' && (
						<p className="font-color-theme-danger">{removeDeveloperError}</p>
					)}
				</Modal>
			)}
		</>
	);
};

export default ManageAccess;
